<template>
    <div v-if="isOpen" class="modal-backdrop">
        <div class="modal-docusign">
            <div class="modal-header">
                <span class="header-title">{{ $t('devis.modal.docusign.title', { id: devisNumero }) }}</span>
                <img class="close-icon" @click="close" :src="closeIcon" alt="close" />
            </div>

            <div class="modal-body">
                <div v-if="isLoading" class="spinner-container">
                    <LoaderComponent />
                    <span class="loading-text">{{ $t('devis.modal.docusign.loading') }}</span>
                </div>

                <iframe
                    v-if="url"
                    :src="url"
                    class="docusign-iframe"
                    @load="onIframeLoaded"
                    allow="camera; microphone; geolocation"
                ></iframe>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';

export default defineComponent({
    name: 'DocuSignModal',
    components: {
        LoaderComponent,
    },
    props: {
        isOpen: { type: Boolean, default: false },
        url: { type: String, default: '' },
        devisNumero: { type: [String, Number], default: '' },
    },
    emits: ['close', 'signature-event'],
    setup(props, { emit }) {
        const isLoading = ref(true);

        watch(
            () => props.url,
            () => {
                isLoading.value = true;
            }
        );

        const onIframeLoaded = () => {
            isLoading.value = false;
        };

        const close = () => {
            emit('close');
        };

        const handleDocuSignMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;

            if (event.data && event.data.source === 'DOCUSIGN_IFRAME') {
                emit('signature-event', event.data.event);
            }
        };

        onMounted(() => {
            window.addEventListener('message', handleDocuSignMessage);
        });

        onBeforeUnmount(() => {
            window.removeEventListener('message', handleDocuSignMessage);
        });

        return {
            isLoading,
            onIframeLoaded,
            close,
        };
    },
    computed: {
        closeIcon() {
            return require('@/assets/img/close-white.svg');
        },
    },
});
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    inset: 0;
    background-color: var(--blur, rgba(0, 0, 0, 0.6));
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.modal-docusign {
    background: var(--white, #fff);
    border-radius: 8px;
    width: 90vw;
    max-width: 1200px;
    height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    background-color: var(--bleu-prinicipal);
    position: relative;
}

.header-title {
    font-weight: var(--font-weight-normal, 500);
    font-size: var(--font-size-large, 18px);
    color: var(--white, #fff);
}

.close-icon {
    cursor: pointer;
    width: 16px;
    height: 16px;
}

.modal-body {
    flex: 1;
    position: relative;
    width: 100%;
    height: 100%;
}

.docusign-iframe {
    width: 100%;
    height: 100%;
    border: none;
}

.spinner-container {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    gap: 16px;
    z-index: 10;
}

.loading-text {
    font-size: var(--font-size-small, 14px);
    color: var(--color-principal, #333);
}
</style>
